import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react"

// Same dead band as `useFittedTableHeight`, which consumes this: a scrollbar
// appearing under the body nudges the reading by a pixel or two, and feeding
// that straight back into the body's height would let the two oscillate.
const TOLERANCE = 8

/**
 * Height of whatever a table's container renders after the scrolling body — a
 * paginator, a selection toolbar. `useFittedTableHeight` gives the body every
 * pixel below it, so anything that follows has to be handed back as `reserve`
 * or it is pushed off the bottom of the screen.
 *
 * Measured as the gap between the container's bottom edge and the body's, which
 * does not move when the body is resized — so the two hooks settle rather than
 * chase each other.
 */
export function useTableFooterHeight<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null)
  const [height, setHeight] = useState(0)

  const measure = useCallback(() => {
    const container = ref.current
    const body = container?.querySelector(".ant-table-body")
    if (!container || !body) return

    const next = Math.max(
      0,
      Math.round(
        container.getBoundingClientRect().bottom -
          body.getBoundingClientRect().bottom
      )
    )

    setHeight((current) =>
      Math.abs(current - next) <= TOLERANCE ? current : next
    )
  }, [])

  // Every render: the toolbar appears and disappears with the selection, which
  // changes the footer without resizing the container the observer watches.
  useLayoutEffect(measure)

  useEffect(() => {
    window.addEventListener("resize", measure)

    const observer =
      typeof ResizeObserver === "undefined" || !ref.current
        ? null
        : new ResizeObserver(measure)
    observer?.observe(ref.current as Element)

    return () => {
      window.removeEventListener("resize", measure)
      observer?.disconnect()
    }
  }, [measure])

  return { ref, height }
}
